// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type {
  Document,
  DocumentFileSummary,
} from '@shared/data-access/types/engine-types';

export interface LinkedDocumentFileRow {
  readonly rowKey: string;
  readonly documentId: number;
  readonly documentTitle: string;
  readonly fileName: string;
  readonly fileSize: number;
}

export const buildLinkedDocumentFileRow = (
  document: Document,
  file: DocumentFileSummary,
  fileIndex: number,
): LinkedDocumentFileRow => ({
  rowKey: `${document.id}-${fileIndex}-${file.file_name}`,
  documentId: document.id,
  documentTitle: document.title,
  fileName: file.file_name,
  fileSize: file.file_size,
});

export const flattenLinkedDocumentFiles = (
  documents: readonly Document[],
): LinkedDocumentFileRow[] =>
  documents.flatMap(document =>
    (document.files ?? []).map((file, fileIndex) =>
      buildLinkedDocumentFileRow(document, file, fileIndex),
    ),
  );

export const RELATABLE_DOCUMENTS_SEARCH_THRESHOLD = 5;

export const filterRelatableDocuments = (
  documents: readonly Document[],
  searchQuery: string,
): Document[] => {
  const normalizedQuery = searchQuery.trim().toLowerCase();

  if (!normalizedQuery) {
    return [...documents];
  }

  return documents.filter(document =>
    document.title.toLowerCase().includes(normalizedQuery),
  );
};
